'use strict';

var ObjectHelpers = require('./object_helpers');

// Something that looks like "abc=" or "def:" allowing for whitespaces
var propertyRegEx = /^\s*([^\s]+)\s*(?::|=)\s*$/;

// Object formatting functions
function getObjectConstructorName(obj) {
    var name;
    try {
        name = obj && obj.constructor && obj.constructor.name;
    } catch (e) {
        // Ignore error
    }
    return name || 'Object';
}
exports.getObjectConstructorName = getObjectConstructorName;

function objectHasPrimitiveSerialization(any) {
    // Manual instanceof
    switch (Object.getPrototypeOf(any)) {
        // Number, Boolean and String objects get serialized back to their primitive value
        // Note: we don't handle Symbol since it's not common to have wrapper objects for them
        case Number.prototype:
        case Boolean.prototype:
        case String.prototype:
        case Date.prototype: // Date and Regexp have a good string serialization
        case RegExp.prototype:
            return true;
        default:
    }

    return false;
}
exports.objectHasPrimitiveSerialization = objectHasPrimitiveSerialization;

function primitiveValueOrObjectType(any) {
    var type = typeof any;

    // Handle primitive types and values that don't serialize properly in JSON
    if (type === 'symbol') return String(any);
    if (type === 'undefined') return String(any);
    if (type === 'number' && !isFinite(any)) return String(any);

    // return any other primitive value or null as is
    if (Object(any) !== any) return any;

    // Object wrappers from primitive types
    if (
        any instanceof String ||
        any instanceof Number ||
        any instanceof Boolean
    ) {
        return primitiveValueOrObjectType(any.valueOf());
    }

    if (any instanceof Date) return any.toJSON();

    if (any instanceof RegExp) return String(any);

    if (Array.isArray(any)) return '{{Array[' + any.length + ']}}';

    if (type === 'object') return '{{' + getObjectConstructorName(any) + '}}';

    return undefined;
}
exports.primitiveValueOrObjectType = primitiveValueOrObjectType;

function getSerializableValue(any, extraErrorInfo) {
    var toSerialize;
    var objHasProps = false;
    var idx;

    function forEachProp(value, prop) {
        var propType = typeof prop;
        var primitive;

        if (propType === 'object' || propType === 'function') return;

        primitive = primitiveValueOrObjectType(value);
        if (primitive !== undefined) {
            toSerialize[String(prop)] = primitive;
            objHasProps = true;
        }
    }

    if (Object(any) !== any || objectHasPrimitiveSerialization(any)) {
        return primitiveValueOrObjectType(any);
    } else if (Array.isArray(any)) {
        toSerialize = [];
        // Note: any non integer props added to the array object will be ignored
        Array.prototype.forEach.call(any, forEachProp);
    } else if (typeof Set == 'function' && any instanceof Set) { // eslint-disable-line no-undef
        toSerialize = [];
        idx = 0;
        any.forEach(function forEachSetProp(value) {
            forEachProp(value, idx++, any);
        });
    } else {
        toSerialize = {};
        if (any instanceof Error) {
            // Manually serialize an error since not enumerable
            if (any.name) toSerialize.name = any.name;
            toSerialize.message = any.message;
            if (extraErrorInfo) {
                if (any.stack) {
                    toSerialize.stack = any.stack;
                } else if (any.sourceURL) {
                    toSerialize.sourceURL = any.sourceURL;
                    toSerialize.line = any.line;
                }
                // Add any non-default prop that might be enumarable
                ObjectHelpers.forEach(any, forEachProp);
            }
        } else if (typeof Map == 'function' && any instanceof Map) { // eslint-disable-line no-undef
            any.forEach(forEachProp);
        } else {
            ObjectHelpers.forEach(any, forEachProp);
            // A function object that didn't have any props added onto it.
            if (!objHasProps && typeof any != 'object') return undefined;
        }
    }
    return toSerialize;
}
exports.getSerializableValue = getSerializableValue;

function flattenDataProperty(out, name, value) {
    var constructorName;
    var props;

    var toSerialize = getSerializableValue(value, true);

    // The serialization of a primitive is a primitive so output as-is
    if (Object(value) !== value) {
        out[name] = toSerialize;
        return;
    }

    constructorName = getObjectConstructorName(value);
    if (constructorName && constructorName !== 'Object' && constructorName !== 'Array') {
        out[name + '.constructor.name'] = constructorName;
    }

    if (typeof value == 'function') {
        out[name] = String(value);
    }

    if (typeof toSerialize != 'object' || toSerialize == null) {
        // Object has a primitive serialization, just output as-is
        if (toSerialize != null) out[name] = toSerialize;
    } else if (Array.isArray(toSerialize)) {
        if (toSerialize.length > 0) {
            toSerialize.forEach(function forEachArrayValue(arrValue, index) {
                out[name + '.' + String(index)] = arrValue;
            });
        } else {
            if (constructorName === 'Array') out[name + '.constructor.name'] = constructorName;
            out[name] = '[]';
        }
    } else {
        props = Object.keys(toSerialize);

        if (props.length > 0) {
            props.forEach(function forEachProp(prop) {
                out[name + '.' + prop] = toSerialize[prop];
            });
        } else {
            if (constructorName === 'Object') out[name + '.constructor.name'] = constructorName;
            out[name] = '{}';
        }
    }
}
exports.flattenDataProperty = flattenDataProperty;

function buildDataObjectFromArgs(args) {
    var obj = {};
    var argsLength = args.length;
    var arg;
    var i;
    var match;

    if (argsLength === 0 || argsLength % 2 !== 0) return null;

    for (i = 0; i < argsLength; ++i) {
        arg = args[i];
        if (typeof arg !== 'string') return null;
        match = propertyRegEx.exec(arg);
        if (!match) return null;
        obj[match[1]] = args[++i];
    }
    return obj;
}
exports.buildDataObjectFromArgs = buildDataObjectFromArgs;

function consumeDataObject(args) {
    var data = null;

    if (args.length === 0) {
        // Nothing to consume
    } else if (args.length === 1) {
        // a plain object ?
        if (ObjectHelpers.isPOJO(args[0])) {
            data = args.shift();
        }
    } else {
        // arguments looking like properties ?
        if ((data = buildDataObjectFromArgs(args))) {
            args.splice(0, args.length);
        }
    }

    return data;
}
exports.consumeDataObject = consumeDataObject;

function consumeUnstructuredArgs(args) {
    var dataName;
    var firstArg;
    var data;

    // A single object parameter, let's create a key based on the type
    if (args.length === 1) {
        firstArg = args[0];
        if (typeof firstArg === 'object') {
            if (firstArg instanceof Error) {
                dataName = 'error';
            } else if (Array.isArray(firstArg)) {
                dataName = 'array';
            } else {
                dataName = 'object';
            }
        } else if (typeof args[0] === 'function') {
            dataName = 'function';
        }

        if (dataName) {
            data = {};
            data[dataName] = args.shift();
        }
    }

    // Multiple args, or primitive single arg
    // Extract them into an array
    if (!data) {
        data = {args: args.splice(0, args.length)};
    }

    return data;
}
exports.consumeUnstructuredArgs = consumeUnstructuredArgs;



//////////////////
// WEBPACK FOOTER
// ./~/@getgo/logger/plugins/helpers/log_formatting_helpers.js
// module id = 720
// module chunks = 0